import { AppData, Bill, Budget, SavingsGoal } from './types';

export type NotificationType = 'bill_due' | 'bill_overdue' | 'budget_warning' | 'budget_over' | 'savings_milestone' | 'savings_complete';
export type NotificationLevel = 'info' | 'warning' | 'danger' | 'success';

export interface SmartNotification {
  id: string;
  type: NotificationType;
  level: NotificationLevel;
  title: string;
  message: string;
  actionLabel?: string;
  actionRoute?: string;
}

function getBillNotifications(bills: Bill[]): SmartNotification[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const notifications: SmartNotification[] = [];

  for (const bill of bills) {
    if (bill.paid) continue;
    const due = new Date(bill.dueDate);
    due.setHours(0, 0, 0, 0);
    const daysUntil = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if (daysUntil < 0) {
      notifications.push({
        id: `bill-overdue-${bill.id}`,
        type: 'bill_overdue',
        level: 'danger',
        title: `${bill.name} is overdue`,
        message: `Was due ${Math.abs(daysUntil)} day${Math.abs(daysUntil) !== 1 ? 's' : ''} ago · $${bill.amount.toFixed(2)}`,
        actionLabel: 'View Bills',
        actionRoute: '/bills',
      });
    } else if (daysUntil <= 7) {
      notifications.push({
        id: `bill-due-${bill.id}`,
        type: 'bill_due',
        level: daysUntil <= 2 ? 'warning' : 'info',
        title: `${bill.name} due ${daysUntil === 0 ? 'today' : daysUntil === 1 ? 'tomorrow' : `in ${daysUntil} days`}`,
        message: `$${bill.amount.toFixed(2)} · ${bill.frequency}`,
        actionLabel: 'View Bills',
        actionRoute: '/bills',
      });
    }
  }

  return notifications;
}

function getBudgetNotifications(budgets: Budget[], transactions: AppData['transactions']): SmartNotification[] {
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const notifications: SmartNotification[] = [];

  for (const budget of budgets) {
    if (budget.month !== currentMonth) continue;

    const spent = transactions
      .filter(t => t.type === 'expense' && t.category === budget.category && t.date.startsWith(currentMonth))
      .reduce((s, t) => s + t.amount, 0);

    const pct = budget.monthlyLimit > 0 ? (spent / budget.monthlyLimit) * 100 : 0;

    if (pct >= 100) {
      notifications.push({
        id: `budget-over-${budget.id}`,
        type: 'budget_over',
        level: 'danger',
        title: `${budget.category} over budget`,
        message: `Spent $${spent.toFixed(2)} of $${budget.monthlyLimit.toFixed(2)} (${Math.round(pct)}%)`,
        actionLabel: 'View Budget',
        actionRoute: '/budget',
      });
    } else if (pct >= 80) {
      notifications.push({
        id: `budget-warning-${budget.id}`,
        type: 'budget_warning',
        level: 'warning',
        title: `${budget.category} nearing limit`,
        message: `$${(budget.monthlyLimit - spent).toFixed(2)} remaining (${Math.round(pct)}% used)`,
        actionLabel: 'View Budget',
        actionRoute: '/budget',
      });
    }
  }

  return notifications;
}

function getSavingsNotifications(goals: SavingsGoal[]): SmartNotification[] {
  const notifications: SmartNotification[] = [];
  const milestones = [25, 50, 75, 100];

  for (const goal of goals) {
    if (goal.targetAmount <= 0) continue;
    const pct = (goal.currentAmount / goal.targetAmount) * 100;

    if (pct >= 100) {
      notifications.push({
        id: `savings-complete-${goal.id}`,
        type: 'savings_complete',
        level: 'success',
        title: `🎉 ${goal.name} goal reached!`,
        message: `You've saved $${goal.currentAmount.toFixed(2)} — target met!`,
        actionLabel: 'View Goals',
        actionRoute: '/more/savings',
      });
    } else {
      // Find the highest milestone crossed
      const crossed = milestones.filter(m => pct >= m && m < 100);
      if (crossed.length > 0) {
        const highest = crossed[crossed.length - 1];
        notifications.push({
          id: `savings-milestone-${goal.id}-${highest}`,
          type: 'savings_milestone',
          level: 'success',
          title: `${goal.name} hit ${highest}%`,
          message: `$${goal.currentAmount.toFixed(2)} of $${goal.targetAmount.toFixed(2)} saved`,
          actionLabel: 'View Goals',
          actionRoute: '/more/savings',
        });
      }
    }
  }

  return notifications;
}

export function generateSmartNotifications(data: AppData): SmartNotification[] {
  const all = [
    ...getBillNotifications(data.bills),
    ...getBudgetNotifications(data.budgets, data.transactions),
    ...getSavingsNotifications(data.savingsGoals),
  ];

  // Sort: danger first, then warning, then success, then info
  const levelOrder: Record<NotificationLevel, number> = { danger: 0, warning: 1, success: 2, info: 3 };
  all.sort((a, b) => levelOrder[a.level] - levelOrder[b.level]);

  return all;
}
